import React from 'react'

import type { HTMLAttributes } from 'react'
import styled, { css } from 'styled-components'

import { OutlineEffectStyles } from '@styles'

function CheckmarkIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="12" fill="none">
      <g filter="url(#dropshadow)">
        <path
          fill="currentColor"
          d="M12.281 2.22a.751.751 0 0 1 0 1.061l-6 6a.751.751 0 0 1-1.061 0l-3-3A.751.751 0 0 1 3.28 5.22l2.47 2.468 5.471-5.468a.751.751 0 0 1 1.062 0h-.003Z"
        />
      </g>
      <defs>
        <filter
          id="dropshadow"
          width="14.501"
          height="11.501"
          x="0"
          y="0"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" result="hardAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
          <feOffset />
          <feGaussianBlur stdDeviation="1" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_144_7" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow_144_7" result="shape" />
        </filter>
      </defs>
    </svg>
  )
}

function IndeterminateCheckmarkIcon() {
  return (
    <svg width="12" height="3" viewBox="0 0 12 3" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect y="0.486084" width="12" height="2" rx="1" fill="currentColor" />
    </svg>
  )
}

export const CHECKBOX_CSS_VARIABLES = {
  // Default
  backgroundColor: '--checkbox-background-color',
  borderColor: '--checkbox-border-color',
  color: '--checkbox-color',
  focusBorderColor: '--checkbox-focus-border-color',
  focusOutlineColor: '--checkbox-focus-outline-color',
  hoverBorderColor: '--checkbox-hover-border-color',
  // Checked
  checkedBackgroundColor: '--checkbox-checked-background-color',
  checkedBorderColor: '--checkbox-checked-border-color',
  checkedFocusBorderColor: '--checkbox-checked-focus-border-color',
  checkedHoverBorderColor: '--checkbox-checked-hover-border-color',
  // Disabled
  disabledBackgroundColor: '--checkbox-disabled-background-color',
  disabledBorderColor: '--checkbox-disabled-border-color',
  disabledColor: '--checkbox-disabled-color',
  disabledFocusBorderColor: '--checkbox-disabled-focus-border-color',
  disabledHoverBorderColor: '--checkbox-disabled-hover-border-color',
  // Error
  errorBorderColor: '--checkbox-error-border-color',
  errorFocusBorderColor: '--checkbox-error-focus-border-color',
  errorHoverBorderColor: '--checkbox-error-hover-border-color',
}

export const StyledCheckbox = styled.div`
  ${({ theme }) => css`
    --background-color: var(${CHECKBOX_CSS_VARIABLES.backgroundColor}, ${theme.colors.gray[0]});
    --border-color: var(${CHECKBOX_CSS_VARIABLES.borderColor}, ${theme.colors.gray[40]});
    --color: var(${CHECKBOX_CSS_VARIABLES.color}, ${theme.colors.gray[0]});
    --focus-border-color: var(${CHECKBOX_CSS_VARIABLES.focusBorderColor}, ${theme.colors.primary[30]});
    --focus-outline-color: var(${CHECKBOX_CSS_VARIABLES.focusOutlineColor}, ${theme.colors.primary[20]});
    --hover-border-color: var(${CHECKBOX_CSS_VARIABLES.hoverBorderColor}, ${theme.colors.gray[30]});

    align-items: center;
    background-color: var(--background-color);
    border-color: var(--border-color);
    border-radius: 4px;
    border-style: solid;
    border-width: 1px;
    color: var(--color);
    display: flex;
    height: 20px;
    justify-content: center;
    transition:
      background-color 250ms ease,
      border-color 250ms ease;
    width: 20px;

    &:not([data-disabled='true']) {
      cursor: pointer;
    }

    ${OutlineEffectStyles}

    &:hover {
      border-color: var(--hover-border-color);
    }
    &:focus-within {
      border-color: var(--focus-border-color);
    }
    &:focus-within {
      &::before {
        border-color: var(--focus-outline-color);
      }
    }

    &[data-checked='true'],
    &[data-indeterminate='true'] {
      --background-color: var(${CHECKBOX_CSS_VARIABLES.checkedBackgroundColor}, ${theme.colors.primary[50]});
      --border-color: var(${CHECKBOX_CSS_VARIABLES.checkedBorderColor}, ${theme.colors.primary[50]});
      --focus-border-color: var(${CHECKBOX_CSS_VARIABLES.checkedFocusBorderColor}, ${theme.colors.primary[50]});
      --hover-border-color: var(${CHECKBOX_CSS_VARIABLES.checkedHoverBorderColor}, ${theme.colors.primary[30]});
    }

    &[data-disabled='true'] {
      --background-color: var(${CHECKBOX_CSS_VARIABLES.disabledBackgroundColor}, ${theme.colors.gray[2]});
      --border-color: var(${CHECKBOX_CSS_VARIABLES.disabledBorderColor}, ${theme.colors.gray[40]});
      --color: var(${CHECKBOX_CSS_VARIABLES.disabledColor}, ${theme.colors.gray[40]});
      --focus-border-color: var(${CHECKBOX_CSS_VARIABLES.disabledFocusBorderColor}, ${theme.colors.gray[40]});
      --hover-border-color: var(${CHECKBOX_CSS_VARIABLES.disabledHoverBorderColor}, ${theme.colors.gray[40]});
    }

    &[data-error='true'] {
      --border-color: var(${CHECKBOX_CSS_VARIABLES.errorBorderColor}, ${theme.colors.error[50]});
      --focus-border-color: var(${CHECKBOX_CSS_VARIABLES.errorFocusBorderColor}, ${theme.colors.error[50]});
      --hover-border-color: var(${CHECKBOX_CSS_VARIABLES.errorHoverBorderColor}, ${theme.colors.error[50]});
    }

    input {
      cursor: inherit;
      height: 100%;
      left: 0;
      margin: 0;
      opacity: 0;
      padding: 0;
      position: absolute;
      top: 0;
      width: 100%;
      z-index: 1;
    }
  `}
`

export interface CheckboxProps extends Omit<HTMLAttributes<HTMLInputElement>, 'onChange'> {
  checked?: boolean
  className?: string
  disabled?: boolean
  error?: boolean
  indeterminate?: boolean
  name?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, isChecked: boolean) => void
  title?: string
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, indeterminate = false, disabled = false, checked = false, error = false, onChange, ...rest }, ref) => {
    const defaultRef = React.useRef<HTMLInputElement>(null)
    const resolvedRef = (ref as React.RefObject<HTMLInputElement>) ?? defaultRef

    React.useEffect(() => {
      if (resolvedRef.current) {
        resolvedRef.current.indeterminate = indeterminate
      }
    }, [resolvedRef, indeterminate])

    const icon = indeterminate ? <IndeterminateCheckmarkIcon /> : checked ? <CheckmarkIcon /> : null

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event, event.target.checked)
    }

    return (
      <StyledCheckbox
        className={className}
        data-checked={checked}
        data-error={error}
        data-indeterminate={indeterminate}
        data-disabled={disabled}
      >
        <input
          disabled={disabled}
          onChange={handleChange}
          onClick={e => e.stopPropagation()}
          ref={resolvedRef}
          type="checkbox"
          // If onChange is provided, the component is controlled, otherwise it's uncontrolled
          {...(onChange != null ? { checked } : { defaultChecked: checked })}
          {...rest}
        />
        {icon}
      </StyledCheckbox>
    )
  }
)

Checkbox.displayName = 'Checkbox'
